import { useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Icon } from '../ui/Icon';
import type { LegendEntry } from './paint';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  show: {
    id: 'map.legend.show',
    defaultMessage: 'Показать контуры с этим значением',
    description: 'Всплывающая подсказка строки легенды, когда контуры с этим значением скрыты: нажатие покажет их',
  },
  hide: {
    id: 'map.legend.hide',
    defaultMessage: 'Скрыть контуры с этим значением',
    description: 'Всплывающая подсказка строки легенды: нажатие скроет контуры с этим значением',
  },
  notShown: {
    id: 'map.legend.notShown',
    defaultMessage: 'Слой не показан',
    description: 'Заголовок пояснения вместо легенды, когда слой выбран, но данных для него нет',
  },
});


function swatchStyle(entry: LegendEntry): CSSProperties {
  return {
    background: entry.color === 'transparent' ? 'transparent' : entry.color,
    borderColor: entry.outline ?? 'transparent',
    borderStyle: entry.dashed ? 'dashed' : entry.dotted ? 'dotted' : 'solid',
    borderWidth: entry.outline ? 1.5 : 1,
  };
}

/** Соседние ступени шкалы (`step`) — одной группой: легенда рисует их полосой. */
function groupSteps(entries: LegendEntry[]): (LegendEntry | LegendEntry[])[] {
  const out: (LegendEntry | LegendEntry[])[] = [];
  for (const entry of entries) {
    const last = out[out.length - 1];
    if (entry.step && Array.isArray(last)) last.push(entry);
    else out.push(entry.step ? [entry] : entry);
  }
  return out;
}

export interface MapLegendProps {
  title: string;
  entries: LegendEntry[];
  /** Строка под списком: например «N контуров под наблюдением — показать». */
  action?: ReactNode;
  /**
   * Легенду можно свернуть до строки образцов (agroai-6al.1): свёрнутая она
   * не накрывает кадр, но смысл цвета остаётся на экране — образцы видны,
   * подпись каждого — в подсказке и по раскрытию.
   */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /**
   * Фильтр слоя: позиции с ключом становятся переключателями «показать /
   * скрыть контуры с этим значением».
   */
  hidden?: ReadonlySet<string>;
  onToggle?: (key: string) => void;
}

/**
 * Легенда обязательна: без неё раскраска — просто цветные пятна.
 * Каждая позиция читается подписью, а образец добавляет обводку/паттерн,
 * чтобы смысл выживал в оттенках серого. Список прокручивается: обрезанная
 * легенда хуже отсутствующей — она врёт о наборе значений.
 */
function Mark({ entry }: { entry: LegendEntry }) {
  return (
    <span className="maplegend__mark" aria-hidden="true">
      <span
        className={`maplegend__swatch ${entry.pattern ? `is-${entry.pattern}` : ''}`}
        style={swatchStyle(entry)}
      />
      {entry.dot ? <span className="maplegend__dot" style={{ background: entry.dot }} /> : null}
    </span>
  );
}

export function MapLegend({
  title,
  entries,
  action,
  collapsible = false,
  defaultCollapsed = false,
  hidden,
  onToggle,
}: MapLegendProps) {
  const t = useT();
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

  const head = collapsible ? (
    <button
      type="button"
      className="maplegend__title maplegend__toggle focus-ring"
      aria-expanded={!collapsed}
      onClick={() => setCollapsed((value) => !value)}
    >
      <span>{title}</span>
      {collapsed ? (
        <span className="maplegend__strip" aria-hidden="true">
          {entries.map((entry) => (
            <span key={entry.key ?? entry.label} title={entry.label}>
              <Mark entry={entry} />
            </span>
          ))}
        </span>
      ) : null}
      <Icon icon={collapsed ? ChevronDown : ChevronUp} size={16} />
    </button>
  ) : (
    <div className="maplegend__title">{title}</div>
  );

  return (
    <div className={`maplegend${collapsed ? ' is-collapsed' : ''}`}>
      {head}
      {collapsed ? null : (
        <>
          <ul className="maplegend__list">
            {groupSteps(entries).map((group) => {
              if (Array.isArray(group)) {
                return (
                  <li className="maplegend__row maplegend__ramp" key={`ramp-${group[0]!.key ?? group[0]!.label}`}>
                    {group.map((entry) => {
                      const key = entry.key;
                      const off = key !== undefined && (hidden?.has(key) ?? false);
                      const cell = (
                        <>
                          <span className="maplegend__rampcell" style={{ background: entry.color }} aria-hidden="true" />
                          <span className="maplegend__ramplabel num">{entry.label}</span>
                        </>
                      );
                      return onToggle !== undefined && key !== undefined ? (
                        <button
                          key={key}
                          type="button"
                          className={`maplegend__rampstep focus-ring${off ? ' is-off' : ''}`}
                          aria-pressed={!off}
                          title={off ? t(m.show) : t(m.hide)}
                          onClick={() => onToggle(key)}
                        >
                          {cell}
                        </button>
                      ) : (
                        <span key={key ?? entry.label} className="maplegend__rampstep">{cell}</span>
                      );
                    })}
                  </li>
                );
              }
              const entry = group;
              const key = entry.key;
              const filterable = onToggle !== undefined && key !== undefined;
              const off = key !== undefined && (hidden?.has(key) ?? false);
              const body = (
                <>
                  <Mark entry={entry} />
                  <span className="maplegend__body">
                    <span className="maplegend__label">{entry.label}</span>
                    {entry.note ? <span className="maplegend__note">{entry.note}</span> : null}
                  </span>
                </>
              );
              return (
                <li className={`maplegend__row${off ? ' is-off' : ''}`} key={key ?? entry.label}>
                  {filterable ? (
                    <button
                      type="button"
                      className="maplegend__filter focus-ring"
                      aria-pressed={!off}
                      title={off ? t(m.show) : t(m.hide)}
                      onClick={() => onToggle(key)}
                    >
                      {body}
                    </button>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
          {action ? <div className="maplegend__action">{action}</div> : null}
        </>
      )}
    </div>
  );
}

/**
 * Слой выбран, а питать его нечем: вместо пустой легенды — причина словами.
 * Пустая легенда читалась бы как «значений нет в районе», а это неправда.
 */
export function MapLayerNotice({ title, reason }: { title: string; reason: string }) {
  const t = useT();
  return (
    <div className="maplegend maplegend--notice">
      <div className="maplegend__title">{title}</div>
      <p className="maplegend__label">{t(m.notShown)}</p>
      <p className="maplegend__note">{reason}</p>
    </div>
  );
}
